import React, { ChangeEvent, useEffect, useState } from 'react';
import { Grid, Stack } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { FormControlLabel, FormControlLabelPlacementProps } from '../../atoms/FormControlLabel';
import { Switch } from '../../atoms/Switch';
import { ManualAddress } from '../../molecules/ManualAddress/ManualAddress';
import { MapSearch } from '../../molecules/MapSearch';
import { ColorProps } from '../../../@types/common.type';
import { AddressDataType, StructureAddressProps } from './StructureAddress.props';
import { SelectMenu } from '../../atoms/SelectMenu';
import { ManualAddressData, OptionType } from '../../molecules/ManualAddress';
import { GRID_SPACING } from '../../../configs/ui-constants';
import { useGetCountries, useGetStatesByCountryId } from '../../../queries/address-query';
import { Checkbox, CheckboxColorProps } from '../../atoms/Checkbox';
import { TextField } from '../../atoms/TextField';
import { Button, ButtonVariantProps } from '../../atoms/Button';

export const StructureAddress: React.FC<StructureAddressProps> = ({
  switchLabel,
  addressData,
  onChange,
  addressTypeData,
  disableTypeSelection = false,
  enableContactNumber,
  isService,
  enableAddressContactFields,
  isOneAddress,
  isEditMode,
  hasAddressEditPermission = true,
  isClientCustomerStructureCreation = false,
  onUseLocationName,
  error
}) => {
  const [isActive, setIsActive] = useState(addressData? addressData.isManual : false);
  const [countries, setCountries] = useState<OptionType[]>([]);
  const [states, setStates] = useState<OptionType[]>([]);
  const [countryId, setCountryId] = useState<number>(0);

  const getStatesByCountryIdQuery = useGetStatesByCountryId(countryId);
  const getCountriesQuery = useGetCountries();

  useEffect(() => {
    getCountriesQuery.data && setCountries(getCountriesQuery.data);
  }, [getCountriesQuery.data]);
  
  useEffect(() => {
    getStatesByCountryIdQuery.data && setStates(getStatesByCountryIdQuery.data);
  }, [getStatesByCountryIdQuery.data]);

  useEffect(() => {
    countries && addressData?.countryName && setCountryId(countries?.find(item => item.name === addressData.countryName)?.id ?? 0);
  }, [addressData?.countryName, countries]);
  
  useEffect(() => {
    states && addressData?.stateName && onChange({
      ...addressData,
      stateId: states?.find(item => item.name === addressData.stateName)?.id ?? 0,
    });
  }, [addressData?.stateName, states]);

  useEffect(() => {    
    countryId !== 0 && getStatesByCountryIdQuery.refetch();
    addressData && onChange({
      ...addressData,
      countryId: countryId,
    });
  }, [countryId]);

  useEffect(() => {
    if (isOneAddress && (isEditMode || isClientCustomerStructureCreation)) {
      setIsActive(addressData?.isManual);
      addressData && onChange({
        ...addressData,
        isManual: addressData?.isManual
      });
    }
  }, [addressData?.isManual]);

  const handleIsActiveChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsActive(event.target.checked);
    addressData && onChange({
      ...addressData,
      isManual: event.target.checked,
    });
  };

  return (
    <Stack direction="row" mb={1} alignItems="center" justifyContent="center">
      <Grid container spacing={GRID_SPACING} pt="2px" width="100%">
        {isActive ? (
          <Grid size={{ xs: 11.7 }}>
            <ManualAddress
              manualAddressData={addressData as ManualAddressData}
              onChange={(data) => onChange(data as AddressDataType)}
              addressTypeData={addressTypeData}
              disableTypeSelection={disableTypeSelection}
              countries={countries}
              states={states}
              enableContactNumber={enableContactNumber}
              enableAddressContactFields={enableAddressContactFields}
              hasAddressEditPermission={hasAddressEditPermission}
              isClientCustomerStructureCreation={isClientCustomerStructureCreation}
              error={!!error}
            />
          </Grid>
        ) : (
          <>
            <Grid size={{ xs: isService? 11.7 : 8.7 }}>
              <MapSearch
                disabled={!hasAddressEditPermission}
                label="Google Address" 
                addressData={addressData as AddressDataType} 
                selectedValue={addressData?.googleAddress} 
                onChange={(data) => onChange(data as AddressDataType)}
                hasError={!!error && !addressData?.googleAddress}
                required={isClientCustomerStructureCreation}
              />
            </Grid>
            {!isService &&
              <Grid size={{ xs: 3 }}>
                <SelectMenu
                  label="Type"
                  id="address-type"
                  items={addressTypeData}
                  disabled={disableTypeSelection || !hasAddressEditPermission}
                  selectedValue={addressData?.addressType}
                  onChange={(text) => {
                    onChange({
                      ...addressData,
                      addressType: text
                    });
                  }}
                />
              </Grid>
            }
            {enableAddressContactFields &&
             <>
               <Grid
                 size={{
                   xs: 12,
                   md: 4
                 }}>
                 <TextField
                   fullWidth
                   disabled={!hasAddressEditPermission}
                   label="Contact Name"
                   value={addressData?.name}
                   onChange={(text) => {
                     addressData && onChange({
                       ...addressData,
                       name: text
                     });
                   }}
                   required={isClientCustomerStructureCreation}
                   error={!!error && !addressData?.name}
                 />
               </Grid>
               <Grid
                 size={{
                   xs: 12,
                   md: 4
                 }}>
                 <TextField
                   fullWidth
                   disabled={!hasAddressEditPermission}
                   label="Contact Number"
                   value={addressData?.contactNumber}
                   onChange={(text) => {
                     addressData && onChange({
                       ...addressData,
                       contactNumber: text
                     });
                   }}
                   required={isClientCustomerStructureCreation}
                   error={!!error && !addressData?.contactNumber}
                 />
               </Grid>
               {isClientCustomerStructureCreation &&
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <TextField
                    fullWidth
                    disabled={!hasAddressEditPermission}
                    label="Email"
                    value={addressData?.email}
                    onChange={(text) => {
                      addressData && onChange({
                        ...addressData,
                        email: text
                      });
                    } } />
                </Grid>
               }
               <Grid
                 size={{
                   xs: 12,
                   md: 4
                 }}>
                 <TextField
                   fullWidth
                   disabled={!hasAddressEditPermission}
                   label="Contact Company"
                   value={addressData?.contactCompany}
                   onChange={(text) => {
                     addressData && onChange({
                       ...addressData,
                       contactCompany: text
                     });
                   }}
                   required={isClientCustomerStructureCreation}
                   InputLabelProps={{
                     shrink: !!addressData?.contactCompany
                   }}
                   error={!!error && !addressData?.contactCompany}
                 />
               </Grid>
               {isClientCustomerStructureCreation &&
                <Grid
                  alignContent="center"
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <Button 
                    variant={ButtonVariantProps.Secondary}
                    startIcon={<ArrowBack/>}
                    onClick={() => onUseLocationName && onUseLocationName()}
                  >
                    Use Location Name
                  </Button>
                </Grid>
               }
             </>
            }
          </>
        )}
      </Grid>
      <Grid ml={2}>
        <FormControlLabel
          control={
            <Switch
              disabled={!hasAddressEditPermission}
              color={ColorProps.Success}
              onChange={handleIsActiveChange}
              checked={addressData?.isManual}
            />
          }
          label={switchLabel}
        />
      </Grid>
      {isService &&
        <FormControlLabel
          value="start"
          control={
            <Checkbox
              disabled={!hasAddressEditPermission}
              checked={addressData?.isKeep}
              onChange={() => addressData && onChange({ ...addressData, isKeep: !addressData.isKeep })}
              color={CheckboxColorProps.Success}
            />
          }
          label="Keep"
          labelPlacement={FormControlLabelPlacementProps.Start}
        />
      }
    </Stack>
  );
};